import React, { useMemo } from "react";

/**
 * A member's handicap index over time.
 *
 * Drawn by hand rather than with a charting library: it's one line and a
 * few dots, and a library would be a few hundred kilobytes for a graph
 * most people look at once.
 *
 * Lower is better in golf, so the axis is inverted — a cut goes up the
 * page, which is how a player thinks about it. Getting that backwards
 * would make an improving golfer look like a declining one.
 */
export default function HandicapChart({ history, height = 150 }) {
  const points = useMemo(
    () => (history ?? [])
      .filter((h) => h.handicap_index != null)
      .map((h) => ({ v: Number(h.handicap_index), t: new Date(h.changed_at) })),
    [history]);

  if (points.length === 0) {
    return <p className="muted" style={{ fontSize: ".8125rem" }}>No handicap recorded yet.</p>;
  }
  // One entry is the normal state for a society that's just started
  // recording. Saying so beats hiding the section, which looks like a
  // fault rather than "nothing has happened yet".
  if (points.length === 1) {
    return (
      <div className="hc-single">
        <b>{points[0].v}</b>
        <small>
          unchanged since {points[0].t.toLocaleDateString("en-GB",
            { day: "numeric", month: "short", year: "numeric" })}
          {" · a line appears here once it changes"}
        </small>
      </div>
    );
  }

  const W = 320, H = height, PAD = { t: 14, r: 10, b: 22, l: 30 };
  const vals = points.map((p) => p.v);
  const lo = Math.floor(Math.min(...vals) - 1);
  const hi = Math.ceil(Math.max(...vals) + 1);
  const t0 = points[0].t.getTime();
  const t1 = points[points.length - 1].t.getTime();
  const span = Math.max(1, t1 - t0);

  const x = (t) => PAD.l + ((t.getTime() - t0) / span) * (W - PAD.l - PAD.r);
  // inverted: a lower index sits higher up
  const y = (v) => PAD.t + ((v - lo) / (hi - lo)) * (H - PAD.t - PAD.b);

  const line = points.map((p, i) => `${i ? "L" : "M"}${x(p.t).toFixed(1)},${y(p.v).toFixed(1)}`).join(" ");
  const first = points[0], last = points[points.length - 1];
  const moved = +(last.v - first.v).toFixed(1);

  return (
    <div className="hc">
      <svg viewBox={`0 0 ${W} ${H}`} className="hc-svg" role="img"
           aria-label={`Handicap index from ${first.v} to ${last.v}`}>
        {[lo, (lo + hi) / 2, hi].map((v) => (
          <g key={v}>
            <line x1={PAD.l} x2={W - PAD.r} y1={y(v)} y2={y(v)} className="hc-grid" />
            <text x={PAD.l - 6} y={y(v) + 3} className="hc-axis" textAnchor="end">{v}</text>
          </g>
        ))}

        <path d={line} className="hc-line" />
        {points.map((p, i) => (
          <circle key={i} cx={x(p.t)} cy={y(p.v)} r={i === points.length - 1 ? 4 : 2.5}
                  className={i === points.length - 1 ? "hc-dot hc-now" : "hc-dot"} />
        ))}

        <text x={PAD.l} y={H - 6} className="hc-axis">
          {first.t.toLocaleDateString("en-GB", { month: "short", year: "2-digit" })}
        </text>
        <text x={W - PAD.r} y={H - 6} className="hc-axis" textAnchor="end">
          {last.t.toLocaleDateString("en-GB", { month: "short", year: "2-digit" })}
        </text>
      </svg>

      <div className="hc-foot">
        <span><small>Now</small><b>{last.v}</b></span>
        <span><small>Then</small><b>{first.v}</b></span>
        <span>
          <small>Change</small>
          <b className={moved < 0 ? "cut" : moved > 0 ? "up" : ""}>
            {moved === 0 ? "—" : moved > 0 ? `+${moved}` : moved}
          </b>
        </span>
      </div>
    </div>
  );
}
